import React, { useEffect, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";

import { IoMdClose } from "react-icons/io";

import {
  CheckCircle,
  ArrowRight,
  UserRound,
  Target,
  PhoneCall,
  ShieldCheck,
} from "lucide-react";

import ContactForm from "./ContactForm";

/* ============================================================
   BACKDROP ANIMATION
============================================================ */

const backdropVariants = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      duration: 0.25,
    },
  },

  exit: {
    opacity: 0,

    transition: {
      duration: 0.2,
    },
  },
};

/* ============================================================
   MODAL ANIMATION
============================================================ */

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.97,
    y: 20,
  },

  visible: {
    opacity: 1,
    scale: 1,
    y: 0,

    transition: {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1],
    },
  },

  exit: {
    opacity: 0,
    scale: 0.97,
    y: 20,

    transition: {
      duration: 0.25,
      ease: "easeInOut",
    },
  },
};

/* ============================================================
   CONTACT MODAL
============================================================ */

const ContactModal = ({
  isOpen,
  onClose,

  initialService = "",
  initialSubService = "",

  lockService = false,
  lockSubService = false,
}) => {
  const [success, setSuccess] = useState(false);

  /* ==========================================================
     RESET SUCCESS STATE
  ========================================================== */

  useEffect(() => {
    if (!isOpen) {
      setSuccess(false);
    }
  }, [isOpen]);

  /* ==========================================================
     CLOSE ON ESC
  ========================================================== */

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  /* ==========================================================
     PREVENT BODY SCROLL
  ========================================================== */

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  /* ==========================================================
     SUCCESS
  ========================================================== */

  const handleSuccess = () => {
    setSuccess(true);

    setTimeout(() => {
      setSuccess(false);
      onClose();
    }, 3000);
  };

  /* ==========================================================
     STEPS
  ========================================================== */

  const steps = [
    {
      number: "01",
      icon: UserRound,
      title: "Share your details",
      description:
        "Tell us who you are and how our team can reach you.",
    },

    {
      number: "02",
      icon: Target,
      title: "Choose your requirement",
      description:
        "Select the service that best matches your business goal.",
    },

    {
      number: "03",
      icon: PhoneCall,
      title: "Let's connect",
      description:
        "Our team will review your enquiry and contact you shortly.",
    },
  ];

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* ==================================================
              BACKDROP
          ================================================== */}

          <motion.div
            className="
              fixed
              inset-0
              z-[999]
              bg-black/70
              backdrop-blur-md
            "
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          {/* ==================================================
              MODAL WRAPPER
          ================================================== */}

          <div
            className="
              fixed
              inset-0
              z-[1000]
              flex
              items-center
              justify-center
              p-1.5

              sm:p-4
              md:p-5
            "
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="
                relative
                flex
                w-full
                max-w-[965px]

                /*
                  Mobile:
                  Give the modal a controlled height so the
                  form can scroll inside it instead of pushing
                  the modal outside the viewport.
                */
                h-[calc(100vh-12px)]
                max-h-[96vh]
                min-h-0

                overflow-hidden
                rounded-[16px]
                border
                border-[var(--border-primary)]
                bg-[var(--bg-primary)]
                shadow-[0_35px_100px_rgba(0,0,0,0.45)]

                sm:h-[calc(100vh-32px)]
                sm:rounded-[20px]

                md:h-auto
              "
              onClick={(e) => e.stopPropagation()}
            >
              {!success ? (
                <>
                  {/* ==================================================
                      LEFT INFORMATION PANEL
                  ================================================== */}

                  <aside
                    className="
                      relative
                      hidden
                      w-[34%]
                      shrink-0
                      overflow-hidden
                      bg-[#ff7417]
                      text-white

                      lg:flex
                      lg:flex-col
                      lg:justify-between
                      lg:p-7

                      xl:p-8
                    "
                  >
                    {/* ==================================================
                        DECORATIVE ELEMENTS
                    ================================================== */}

                    <div
                      className="
                        pointer-events-none
                        absolute
                        -right-24
                        -top-24
                        h-64
                        w-64
                        rounded-full
                        bg-white/10
                        blur-3xl
                      "
                    />

                    <div
                      className="
                        pointer-events-none
                        absolute
                        -bottom-28
                        -left-20
                        h-64
                        w-64
                        rounded-full
                        border
                        border-white/20
                      "
                    />

                    <div
                      className="
                        pointer-events-none
                        absolute
                        bottom-20
                        -right-14
                        h-36
                        w-36
                        rounded-full
                        border
                        border-white/20
                      "
                    />

                    {/* ==================================================
                        TOP CONTENT
                    ================================================== */}

                    <div className="relative z-10">
                      {/* BADGE */}

                      <div
                        className="
                          inline-flex
                          items-center
                          gap-2
                          rounded-full
                          border
                          border-white/35
                          bg-white/10
                          px-3
                          py-1.5
                          text-[9px]
                          font-bold
                          uppercase
                          tracking-[0.17em]
                          text-white
                        "
                      >
                        <span
                          className="
                            h-1.5
                            w-1.5
                            rounded-full
                            bg-white
                          "
                        />

                        Let's build something
                      </div>

                      {/* HEADING */}

                      <h2
                        className="
                          mt-6
                          max-w-[285px]
                          text-[29px]
                          font-extrabold
                          leading-[1.08]
                          tracking-[-0.035em]
                          text-white

                          xl:text-[32px]
                        "
                      >
                        Have an idea?
                        <br />
                        Let's make it real.
                      </h2>

                      {/* DESCRIPTION */}

                      <p
                        className="
                          mt-4
                          max-w-[285px]
                          text-[12px]
                          leading-[1.7]
                          text-white/80

                          xl:text-[13px]
                        "
                      >
                        Tell us what you're looking to build,
                        improve, or grow. We'll help you find the
                        right solution.
                      </p>

                      {/* ==================================================
                          STEPS
                      ================================================== */}

                      <div className="mt-8 space-y-6">
                        {steps.map((step, index) => {
                          const Icon = step.icon;

                          return (
                            <div
                              key={step.number}
                              className="
                                relative
                                flex
                                gap-3.5
                              "
                            >
                              {/* CONNECTOR */}

                              {index !== steps.length - 1 && (
                                <div
                                  className="
                                    absolute
                                    left-[15px]
                                    top-[31px]
                                    h-[calc(100%+17px)]
                                    w-px
                                    bg-white/25
                                  "
                                />
                              )}

                              {/* NUMBER */}

                              <div
                                className="
                                  relative
                                  z-10
                                  flex
                                  h-[31px]
                                  w-[31px]
                                  shrink-0
                                  items-center
                                  justify-center
                                  rounded-full
                                  bg-white
                                  text-[9px]
                                  font-extrabold
                                  text-[#ff7417]
                                  shadow-[0_5px_20px_rgba(0,0,0,0.08)]
                                "
                              >
                                {step.number}
                              </div>

                              {/* CONTENT */}

                              <div className="min-w-0 pt-0.5">
                                <div className="flex items-center gap-1.5">
                                  <h3
                                    className="
                                      text-[12px]
                                      font-bold
                                      text-white
                                    "
                                  >
                                    {step.title}
                                  </h3>

                                  <Icon
                                    className="
                                      h-3.5
                                      w-3.5
                                      text-white
                                    "
                                    strokeWidth={2}
                                  />
                                </div>

                                <p
                                  className="
                                    mt-1
                                    max-w-[235px]
                                    text-[10px]
                                    leading-[1.7]
                                    text-white/65
                                  "
                                >
                                  {step.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ==================================================
                        BOTTOM PRIVACY
                    ================================================== */}

                    <div
                      className="
                        relative
                        z-10
                        border-t
                        border-white/20
                        pt-4
                      "
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck
                          className="
                            h-3.5
                            w-3.5
                            text-white
                          "
                          strokeWidth={2}
                        />

                        <p
                          className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.15em]
                            text-white
                          "
                        >
                          Your information is private
                        </p>
                      </div>

                      <p
                        className="
                          mt-1
                          text-[10px]
                          leading-5
                          text-white/65
                        "
                      >
                        No spam. No obligation. Just a genuine
                        conversation.
                      </p>
                    </div>
                  </aside>

                  {/* ==================================================
                      MOBILE HEADER
                  ================================================== */}

                  <div
                    className="
                      absolute
                      left-0
                      right-0
                      top-0
                      z-20
                      block
                      bg-[#ff7417]
                      px-4
                      py-4
                      text-white

                      sm:px-5
                      sm:py-5

                      lg:hidden
                    "
                  >
                    <div
                      className="
                        pr-10
                        text-[8px]
                        font-bold
                        uppercase
                        tracking-[0.17em]
                        text-white/90

                        sm:text-[9px]
                      "
                    >
                      Let's build something
                    </div>

                    <h2
                      className="
                        mt-1
                        max-w-[270px]
                        pr-8
                        text-[18px]
                        font-extrabold
                        leading-[1.15]
                        tracking-[-0.025em]

                        sm:mt-1.5
                        sm:max-w-[320px]
                        sm:text-[21px]
                      "
                    >
                      Have an idea?
                      <br />
                      Let's make it real.
                    </h2>

                    <p
                      className="
                        mt-1.5
                        max-w-[430px]
                        pr-8
                        text-[9px]
                        leading-4
                        text-white/75

                        sm:mt-2
                        sm:text-[10px]
                        sm:leading-5
                      "
                    >
                      Tell us what you're looking to build,
                      improve, or grow.
                    </p>
                  </div>

                  {/* ==================================================
                      RIGHT FORM PANEL
                  ================================================== */}

                  <section
                    className="
                      relative
                      min-h-0
                      min-w-0
                      flex-1

                      overflow-x-hidden
                      overflow-y-auto

                      custom-scrollbar
                      bg-[var(--bg-primary)]

                      lg:overflow-y-hidden
                    "
                  >
                    {/* ==================================================
                        CLOSE BUTTON
                    ================================================== */}

                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Close form"
                      className="
                        absolute
                        right-2.5
                        top-2.5
                        z-30
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-white/30
                        bg-black/10
                        text-white
                        transition-all
                        duration-200

                        hover:border-[var(--brand-primary)]
                        hover:bg-[var(--brand-primary)]/10
                        hover:text-[var(--brand-primary)]

                        active:scale-95

                        sm:right-3
                        sm:top-3
                        sm:h-9
                        sm:w-9

                        lg:right-4
                        lg:top-4
                        lg:border-[var(--border-primary)]
                        lg:bg-[var(--bg-secondary)]
                        lg:text-[var(--text-muted)]
                      "
                    >
                      <IoMdClose size={18} />
                    </button>

                    {/* ==================================================
                        FORM CONTENT
                    ================================================== */}

                    <div
                      className="
                        w-full
                        px-4
                        pb-5
                        pt-[126px]

                        sm:px-5
                        sm:pb-6
                        sm:pt-[138px]

                        md:px-6

                        lg:px-8
                        lg:pb-6
                        lg:pt-6
                      "
                    >
                      {/* ==================================================
                          DESKTOP HEADER
                      ================================================== */}

                      <div
                        className="
                          mb-4
                          hidden
                          pr-12
                          lg:block
                        "
                      >
                        <div
                          className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.16em]
                            text-[var(--brand-primary)]
                          "
                        >
                          Let's get started
                        </div>

                        <h2
                          className="
                            mt-1
                            text-[24px]
                            font-extrabold
                            leading-tight
                            tracking-[-0.025em]
                            text-[var(--text-primary)]
                          "
                        >
                          Tell us about your project
                        </h2>

                        <p
                          className="
                            mt-1.5
                            text-[11px]
                            leading-5
                            text-[var(--text-muted)]
                          "
                        >
                          A few details are all we need to get the
                          conversation started.
                        </p>
                      </div>

                      {/* ==================================================
                          FORM
                      ================================================== */}

                      <ContactForm
                        onSuccess={handleSuccess}
                        initialService={initialService}
                        initialSubService={initialSubService}
                        lockService={lockService}
                        lockSubService={lockSubService}
                      />
                    </div>
                  </section>
                </>
              ) : (
                /* ==================================================
                   SUCCESS STATE
                ================================================== */

                <div
                  className="
                    flex
                    min-h-[430px]
                    w-full
                    items-center
                    justify-center
                    bg-[var(--bg-primary)]
                    px-6
                    py-12
                  "
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="
                      flex
                      max-w-md
                      flex-col
                      items-center
                      text-center
                    "
                  >
                    <motion.div
                      initial={{
                        scale: 0.7,
                        opacity: 0,
                      }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                      }}
                      transition={{
                        duration: 0.4,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="
                        flex
                        h-20
                        w-20
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-[var(--brand-primary)]/30
                        bg-[var(--brand-primary)]/10
                      "
                    >
                      <CheckCircle
                        className="
                          h-10
                          w-10
                          text-[var(--brand-primary)]
                        "
                        strokeWidth={2}
                      />
                    </motion.div>

                    <motion.div
                      initial={{
                        y: 10,
                        opacity: 0,
                      }}
                      animate={{
                        y: 0,
                        opacity: 1,
                      }}
                      transition={{
                        delay: 0.1,
                        duration: 0.35,
                      }}
                    >
                      <h2
                        className="
                          mt-6
                          text-2xl
                          font-bold
                          tracking-tight
                          text-[var(--text-primary)]
                        "
                      >
                        Thank you!
                      </h2>

                      <p
                        className="
                          mt-3
                          text-sm
                          leading-6
                          text-[var(--text-secondary)]
                        "
                      >
                        Your enquiry has been successfully
                        submitted. Our team will get back to you
                        shortly.
                      </p>

                      <div
                        className="
                          mt-6
                          inline-flex
                          items-center
                          gap-2
                          rounded-full
                          border
                          border-[var(--brand-primary)]/20
                          bg-[var(--brand-primary)]/5
                          px-4
                          py-2
                          text-xs
                          font-medium
                          text-[var(--brand-primary)]
                        "
                      >
                        We'll take it from here

                        <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </motion.div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;